import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { 
  FiUserPlus, 
  FiSearch, 
  FiEdit2, 
  FiTrash2, 
  FiEye, 
  FiMessageSquare, 
  FiClipboard, 
  FiX, 
  FiSend, 
  FiUsers, 
  FiHome, 
  FiClock, 
  FiCalendar,
  FiUploadCloud,
  FiFileText,
  FiCheckCircle,
  FiCheck,
  FiRefreshCw,
  FiLogOut,
  FiAlertCircle
} from 'react-icons/fi';
import * as xlsx from 'xlsx';
import { formatDate, getDateInputValue, isSameDayIST } from '../utils/dateUtils';
import { getWhatsAppErrorMessage } from '../utils/formatUtils';
import { useAuth } from '../context/AuthContext';

// Format phone number for display
const formatPhoneNumber = (phone) => {
  if (!phone) return '—';
  let cleaned = String(phone).replace(/^91/, '');
  if (cleaned.length === 10) {
    return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  }
  return phone;
};

const formatStaySpan = (checkin, checkout) => {
  if (!checkin && !checkout) return '—';
  if (checkin && !checkout) return formatDate(checkin, 'dd MMM');
  if (!checkin && checkout) return formatDate(checkout, 'dd MMM');

  const inStr = formatDate(checkin, 'dd MMM');
  const outStr = formatDate(checkout, 'dd MMM');
  const inParts = inStr.split(' ');
  const outParts = outStr.split(' ');

  if (inParts[1] && outParts[1] && inParts[1] === outParts[1]) {
    return `${inParts[0]}–${outParts[0]} ${inParts[1]}`;
  }
  return `${inStr} – ${outStr}`;
};

function Guests() {
  const { user: authUser } = useAuth();
  const [guests, setGuests] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [guestDetails, setGuestDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    room_no: '',
    checkin: getDateInputValue(new Date()),
    checkout: '',
    dob: '',
    anniversary: '',
    sendWelcome: true
  });
  const [messageText, setMessageText] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [submitting, setSubmitting] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sendingWelcomeGuestId, setSendingWelcomeGuestId] = useState(null);
  const welcomeClickLockRef = useRef({});
  const [lastCreatedRoom, setLastCreatedRoom] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDobRequired, setIsDobRequired] = useState(true);

  // Expected Arrivals & PMS Excel Import State
  const [expectedArrivals, setExpectedArrivals] = useState([]);
  const [arrivalsLoading, setArrivalsLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [selectedArrival, setSelectedArrival] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkinFormData, setCheckinFormData] = useState({
    name: '',
    phone: '',
    room_no: '',
    checkout: '',
    sendWelcome: true
  });
  const fileInputRef = useRef(null);
  const guestCacheRef = useRef({});
  const arrivalsCacheRef = useRef(null);

  const fetchGuests = useCallback(async (tabOverride, forceRefresh = false) => {
    const targetTab = tabOverride !== undefined ? tabOverride : activeTab;
    if (targetTab === 'arrivals') return;

    if (guestCacheRef.current[targetTab] && !forceRefresh) {
      setGuests(guestCacheRef.current[targetTab]);
      setLoading(false);
    } else {
      setLoading(true);
    }

    try {
      const status = targetTab === 'active' ? 'active' : targetTab === 'upcoming' ? 'upcoming' : targetTab === 'inactive' ? 'inactive' : null;
      const url = status ? `/api/admin/guests?status=${status}` : '/api/admin/guests';
      const response = await api.get(url);
      const data = Array.isArray(response.data) ? response.data : [];
      guestCacheRef.current[targetTab] = data;
      setGuests(data);
    } catch (error) {
      console.error('Failed to fetch guests:', error);
      setMessage({ type: 'error', text: 'Failed to load guests' });
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  const fetchExpectedArrivals = useCallback(async (forceRefresh = false) => {
    if (arrivalsCacheRef.current && !forceRefresh) {
      setExpectedArrivals(arrivalsCacheRef.current);
      setArrivalsLoading(false);
    } else {
      setArrivalsLoading(true);
    }

    try {
      const response = await api.get('/api/admin/guests/expected-arrivals?status=pending');
      const data = Array.isArray(response.data) ? response.data : [];
      arrivalsCacheRef.current = data;
      setExpectedArrivals(data);
    } catch (error) {
      console.error('Failed to fetch expected arrivals:', error);
    } finally {
      setArrivalsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGuests();
  }, [fetchGuests]);

  useEffect(() => {
    fetchExpectedArrivals();
  }, [fetchExpectedArrivals]);

  useEffect(() => {
    const fetchGuestRegistrationSettings = async () => {
      try {
        const response = await api.get('/api/public/guest-registration-settings');
        setIsDobRequired(response.data?.dob_required_for_guest_creation !== false);
      } catch (error) {
        setIsDobRequired(true);
      }
    };

    fetchGuestRegistrationSettings();
  }, []);

  const fetchGuestDetails = async (guestId) => {
    try {
      setDetailsLoading(true);
      const response = await api.get(`/api/admin/guests/${guestId}`);
      setGuestDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch guest details:', error);
      setMessage({ type: 'error', text: 'Failed to load guest details' });
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleViewDetails = async (guest) => {
    setSelectedGuest(guest);
    setShowDetailsModal(true);
    await fetchGuestDetails(guest.id);
  };

  const handleEdit = (guest) => {
    setSelectedGuest(guest);
    setIsEditMode(true);
    setFormData({
      name: guest.name || '',
      phone: guest.phone ? guest.phone.replace(/^91/, '') : '',
      room_no: guest.room_no || '',
      checkin: guest.checkin ? guest.checkin.split('T')[0] : '',
      checkout: guest.checkout ? guest.checkout.split('T')[0] : '',
      dob: guest.dob ? guest.dob.split('T')[0] : '',
      anniversary: guest.anniversary ? guest.anniversary.split('T')[0] : '',
      sendWelcome: false
    });
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const handleDelete = (guest) => {
    if (authUser?.role !== 'admin') {
      setMessage({ type: 'error', text: 'Only admin users are permitted to delete active or inactive guests.' });
      return;
    }
    setSelectedGuest(guest);
    setShowDeleteModal(true);
  };

  const handleSendMessage = (guest) => {
    setSelectedGuest(guest);
    setMessageText('');
    setShowMessageModal(true);
    setMessage({ type: '', text: '' });
  };

  const confirmDelete = async () => {
    if (authUser?.role !== 'admin') {
      setMessage({ type: 'error', text: 'Only admin users are permitted to delete active or inactive guests.' });
      setShowDeleteModal(false);
      return;
    }
    try {
      setSubmitting(true);
      await api.delete(`/api/admin/guests/${selectedGuest.id}`);
      setMessage({ type: 'success', text: 'Guest deleted successfully' });
      setShowDeleteModal(false);
      setSelectedGuest(null);
      fetchGuests();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to delete guest';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendWelcomeMessage = async (guest) => {
    if (!guest || !guest.id) return;

    // Prevent rapid repeat clicks on the same guest within 3 seconds
    const now = Date.now();
    if (welcomeClickLockRef.current[guest.id] && (now - welcomeClickLockRef.current[guest.id]) < 3000) {
      return;
    }
    welcomeClickLockRef.current[guest.id] = now;

    if (sendingWelcomeGuestId === guest.id) return;

    try {
      setSendingWelcomeGuestId(guest.id);
      setMessage({ type: '', text: '' });

      // Optimistically mark this guest as sent immediately so button instantly transforms to 'Sent' badge
      setGuests(prevGuests => (prevGuests || []).map(g => 
        g.id === guest.id 
          ? { ...g, welcomeMessageStatus: 'sent', pendingWelcomeMessage: false, isWelcomeFailed: false } 
          : g
      ));
      
      const res = await api.post('/api/welcome/send', {
        guestId: guest.id,
        useTemplate: true // Force template usage for check-in day guests
      });
      
      setMessage({ 
        type: 'success', 
        text: res.data?.message || 'Welcome message dispatched to WhatsApp! Verifying delivery...' 
      });
      
      // Refresh guests list at 1.5s and 4s for delivery webhook
      setTimeout(() => {
        fetchGuests(undefined, true);
      }, 1500);

      setTimeout(() => {
        fetchGuests(undefined, true);
      }, 4000);
    } catch (error) {
      // Revert optimistic update on failure so staff can see failure badge and retry button
      setGuests(prevGuests => (prevGuests || []).map(g => 
        g.id === guest.id 
          ? { 
              ...g, 
              welcomeMessageStatus: null, 
              pendingWelcomeMessage: true, 
              isWelcomeFailed: true, 
              welcomeMessageError: { message: error.response?.data?.error || error.message } 
            } 
          : g
      ));

      let errorMessage = 'Failed to send welcome message';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (errorData.code === 'WHATSAPP_AUTH_ERROR' || errorData.code === 'WHATSAPP_NOT_CONFIGURED') {
          errorMessage = 'WhatsApp service is not properly configured. Please contact your administrator.';
        } else if (errorData.code === '24_HOUR_RULE' || error.message?.includes('24 hours')) {
          errorMessage = 'Cannot send message due to WhatsApp 24-hour messaging rule. The guest needs to message you first, or you need to use an approved WhatsApp template message.';
        } else {
          errorMessage = errorData.error || errorData.message || 'Failed to send welcome message';
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSendingWelcomeGuestId(null);
    }
  };

  const handleSendMessageSubmit = async (e) => {
    e.preventDefault();
    if (!messageText.trim()) return;

    try {
      setSendingMessage(true);
      setMessage({ type: '', text: '' }); // Clear previous messages
      await api.post(`/api/admin/guests/${selectedGuest.id}/send-message`, {
        message: messageText.trim()
      });
      setMessage({ type: 'success', text: 'Message sent successfully' });
      setMessageText('');
      setTimeout(() => {
        setShowMessageModal(false);
        setSelectedGuest(null);
        setMessage({ type: '', text: '' });
      }, 2000);
    } catch (error) {
      let errorMessage = 'Failed to send message';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle specific error codes
        if (errorData.code === 'WHATSAPP_AUTH_ERROR' || errorData.code === 'WHATSAPP_NOT_CONFIGURED') {
          errorMessage = errorData.error || 'WhatsApp service is not properly configured. Please contact your administrator.';
        } else if (errorData.code === '24_HOUR_RULE') {
          errorMessage = errorData.error || 'Cannot send message due to WhatsApp 24-hour messaging rule.';
        } else {
          errorMessage = errorData.error || errorData.message || 'Failed to send message';
        }
        
        // Add details if available
        if (errorData.details && typeof errorData.details === 'string') {
          errorMessage += ` (${errorData.details})`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSendingMessage(false);
    }
  };

  const getMinCheckoutDate = (checkinDateStr) => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const baseStr = checkinDateStr ? checkinDateStr.split('T')[0] : todayStr;
    const baseDate = new Date(baseStr);
    if (isNaN(baseDate.getTime())) return '';
    baseDate.setDate(baseDate.getDate() + 1);
    return baseDate.toISOString().split('T')[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name?.trim() || !formData.phone?.trim() || !formData.room_no?.trim()) {
      setMessage({ type: 'error', text: 'Name, phone number, and room number are all required.' });
      return;
    }

    if (!isEditMode && !formData.checkout?.trim()) {
      setMessage({ type: 'error', text: 'Check-out date is required for walk-in guests.' });
      return;
    }

    // Validate checkout date is strictly after checkin date
    const todayIso = new Date().toISOString().split('T')[0];
    const effCheckinStr = formData.checkin ? formData.checkin.split('T')[0] : todayIso;
    const minCheckoutStr = getMinCheckoutDate(effCheckinStr);

    if (formData.checkout) {
      const checkoutStr = formData.checkout.split('T')[0];
      if (checkoutStr < minCheckoutStr) {
        setMessage({
          type: 'error',
          text: `Check-out date (${checkoutStr}) must be after check-in date (${effCheckinStr}). Same-day or past check-out is not allowed. Please change the check-out date.`
        });
        return;
      }
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      if (isEditMode) {
        const response = await api.put(`/api/admin/guests/${selectedGuest.id}`, formData);
      setMessage({
        type: 'success',
          text: response.data.message || 'Guest updated successfully!'
        });
        setIsEditMode(false);
        setSelectedGuest(null);
        setTimeout(() => {
          setShowModal(false);
          setMessage({ type: '', text: '' });
      setFormData({
        name: '',
        phone: '',
        room_no: '',
        checkin: getDateInputValue(new Date()),
        checkout: '',
        dob: '',
        anniversary: '',
        sendWelcome: true
      });
        }, 1500);
      } else {
        const response = await api.post('/api/admin/guests', formData);
      setMessage({
        type: 'success',
          text: response.data.message || 'Guest created successfully!'
        });
        setLastCreatedRoom(formData.room_no);
        setTimeout(() => {
          setShowModal(false);
          setMessage({ type: '', text: '' });
      setFormData({
        name: '',
        phone: '',
        room_no: '',
        checkin: getDateInputValue(new Date()),
        checkout: '',
        dob: '',
        anniversary: '',
        sendWelcome: true
      });
        }, 1500);
      }

      await fetchGuests();
    } catch (error) {
      console.error('Failed to save guest:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to save guest'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'checkin') {
      const newCheckin = value;
      setFormData(prev => {
        const nextMinCheckout = getMinCheckoutDate(newCheckin);
        let nextCheckout = prev.checkout;
        if (nextCheckout && nextCheckout < nextMinCheckout) {
          setMessage({
            type: 'error',
            text: 'Check-out date must be at least 1 day after check-in date. Please update check-out date.'
          });
          nextCheckout = nextMinCheckout;
        } else {
          setMessage({ type: '', text: '' });
        }
        return {
          ...prev,
          checkin: newCheckin,
          checkout: nextCheckout
        };
      });
      return;
    }

    if (name === 'checkout') {
      const newCheckout = value;
      const todayIso = new Date().toISOString().split('T')[0];
      const minCheckout = getMinCheckoutDate(formData.checkin || todayIso);

      if (newCheckout && newCheckout < minCheckout) {
        setMessage({
          type: 'error',
          text: 'Check-out date cannot be today or in the past. It must be at least 1 day after check-in date.'
        });
      } else {
        setMessage({ type: '', text: '' });
      }

      setFormData(prev => ({
        ...prev,
        checkout: newCheckout
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleModalClose = () => {
    setShowModal(false);
    setIsEditMode(false);
    setSelectedGuest(null);
    setMessage({ type: '', text: '' });
    setFormData({
      name: '',
      phone: '',
      room_no: '',
      checkin: '',
      checkout: '',
      dob: '',
      anniversary: '',
      sendWelcome: true
    });
    setLastCreatedRoom(null);
  };

  /* ------------------------------------------------------------------ */
  /*  PMS Excel Parsing & Fast Arrival Check-In                         */
  /* ------------------------------------------------------------------ */
  const parseExcelData = (data) => {
    const workbook = xlsx.read(data, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const rows = xlsx.utils.sheet_to_json(sheet, { header: 1, defval: '' });

    const parsed = [];

    // Check if PMS format
    let isPmsFormat = false;
    for (let i = 0; i < rows.length; i++) {
      const rowStr = (rows[i] || []).join(' ');
      if (rowStr.includes('Guest Name:')) {
        isPmsFormat = true;
        break;
      }
    }

    if (isPmsFormat) {
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i] || [];
        const guestNameIndex = row.findIndex(c => String(c).trim().toLowerCase().includes('guest name:'));
        if (guestNameIndex !== -1 && i + 1 < rows.length) {
          const nextRow = rows[i + 1] || [];
          let name = String(nextRow[guestNameIndex] || nextRow[1] || '').trim();
          let room_no = String(nextRow[14] || nextRow[12] || '').trim();
          let phone = '';

          for (let c of nextRow) {
            const digits = String(c).replace(/\D/g, '');
            if (digits.length === 10 && /^[6-9]/.test(digits)) {
              phone = digits;
              break;
            }
          }

          let resv_no = '';
          let source = '';
          let departure = '';
          let arrival = '';

          for (let b = i - 1; b >= Math.max(0, i - 6); b--) {
            const backRow = rows[b] || [];
            const bStr = backRow.join(' ');
            if (bStr.includes('Company...:')) {
              const compIdx = backRow.findIndex(c => String(c).includes('Company...:'));
              if (compIdx !== -1 && backRow[compIdx + 1]) {
                source = String(backRow[compIdx + 1]).trim();
              }
            }
            if (String(backRow[0]).trim().match(/^\d{4,10}$/)) {
              resv_no = String(backRow[0]).trim();
              arrival = String(backRow[2] || '').trim();
              departure = String(backRow[5] || '').trim();
              break;
            }
          }

          if (name && name !== 'Guest Name:' && !name.toLowerCase().includes('arranged')) {
            parsed.push({
              resv_no,
              guest_name: name,
              source: source || 'Direct',
              departure: departure || '—',
              arrival: arrival || '—',
              room_no: room_no || '',
              phone: phone || ''
            });
          }
        }
      }
    } else {
      let headerRowIdx = -1;
      for (let i = 0; i < Math.min(15, rows.length); i++) {
        const rowLower = (rows[i] || []).map(c => String(c).toLowerCase());
        if (rowLower.some(c => c.includes('name') || c.includes('guest') || c.includes('resv') || c.includes('room'))) {
          headerRowIdx = i;
          break;
        }
      }

      if (headerRowIdx !== -1) {
        const headers = rows[headerRowIdx].map(c => String(c).trim().toLowerCase());
        const nameIdx = headers.findIndex(h => h.includes('name') || h.includes('guest'));
        const phoneIdx = headers.findIndex(h => h.includes('phone') || h.includes('mobile') || h.includes('contact'));
        const roomIdx = headers.findIndex(h => h.includes('room'));
        const resvIdx = headers.findIndex(h => h.includes('resv') || h.includes('booking') || h.includes('id'));
        const sourceIdx = headers.findIndex(h => h.includes('source') || h.includes('channel') || h.includes('company'));
        const depIdx = headers.findIndex(h => h.includes('dep') || h.includes('checkout') || h.includes('out'));

        for (let i = headerRowIdx + 1; i < rows.length; i++) {
          const row = rows[i];
          if (!row || !row[nameIdx]) continue;
          const name = String(row[nameIdx]).trim();
          if (!name) continue;

          parsed.push({
            resv_no: resvIdx !== -1 ? String(row[resvIdx] || '').trim() : '',
            guest_name: name,
            phone: phoneIdx !== -1 ? String(row[phoneIdx] || '').replace(/\D/g, '') : '',
            room_no: roomIdx !== -1 ? String(row[roomIdx] || '').trim() : '',
            source: sourceIdx !== -1 ? String(row[sourceIdx] || '').trim() : 'Direct',
            departure: depIdx !== -1 ? String(row[depIdx] || '').trim() : '—'
          });
        }
      }
    }
    return parsed;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    try {
      const buffer = await file.arrayBuffer();
      const parsed = parseExcelData(buffer);
      if (parsed.length === 0) {
        setMessage({ type: 'error', text: 'No guest names found in the selected Excel file. Please verify the format.' });
      } else {
        setImportPreview(parsed);
      }
    } catch (err) {
      console.error('Failed to parse Excel:', err);
      setMessage({ type: 'error', text: 'Failed to read Excel file. Please ensure it is a valid .xlsx or .xls file.' });
    }
  };

  const handleConfirmImport = async () => {
    if (importPreview.length === 0) return;
    try {
      setImporting(true);
      const res = await api.post('/api/admin/guests/import-arrivals', { items: importPreview });
      setMessage({ type: 'success', text: `Successfully imported ${res.data.count || importPreview.length} expected arrivals!` });
      setShowImportModal(false);
      setImportFile(null);
      setImportPreview([]);
      setActiveTab('arrivals');
      fetchExpectedArrivals();
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to import arrivals' });
    } finally {
      setImporting(false);
    }
  };

  const parseDepartureToDate = (departure) => {
    if (!departure) return '';
    const depStr = String(departure).trim();
    const num = Number(depStr);
    if (!isNaN(num) && num > 30000 && num < 60000) {
      const utcDays = Math.floor(num - 25569);
      const dateInfo = new Date(utcDays * 86400 * 1000);
      const y = dateInfo.getUTCFullYear();
      const m = String(dateInfo.getUTCMonth() + 1).padStart(2, '0');
      const d = String(dateInfo.getUTCDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(depStr)) {
      return depStr.split('T')[0].split(' ')[0];
    }
    const dmyMatch = depStr.match(/^(\d{1,2})[/\-\.](\d{1,2})[/\-\.](\d{2,4})/);
    if (dmyMatch) {
      const day = dmyMatch[1].padStart(2, '0');
      const month = dmyMatch[2].padStart(2, '0');
      let year = dmyMatch[3];
      if (year.length === 2) year = '20' + year;
      return `${year}-${month}-${day}`;
    }
    const ymdMatch = depStr.match(/^(\d{4})[/\-\.](\d{1,2})[/\-\.](\d{1,2})/);
    if (ymdMatch) {
      const year = ymdMatch[1];
      const month = ymdMatch[2].padStart(2, '0');
      const day = ymdMatch[3].padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return '';
  };

  const formatDepartureDisplay = (departure) => {
    if (!departure || departure === '—') return '—';
    const parsed = parseDepartureToDate(departure);
    if (parsed) {
      const d = new Date(parsed + 'T00:00:00');
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      }
    }
    return String(departure);
  };

  const handleOpenCheckin = (arrival) => {
    setSelectedArrival(arrival);
    const initialRoom = String(arrival.room_no || '').split(',')[0].trim();
    const parsedCheckout = parseDepartureToDate(arrival.departure);
    const todayIso = new Date().toISOString().split('T')[0];
    const minCheckout = getMinCheckoutDate(todayIso);

    let validCheckout = parsedCheckout;
    if (validCheckout && validCheckout < minCheckout) {
      validCheckout = minCheckout;
    }

    setCheckinFormData({
      name: arrival.guest_name || '',
      phone: arrival.phone ? arrival.phone.replace(/^91/, '') : '',
      room_no: initialRoom,
      checkout: validCheckout || '',
      sendWelcome: true
    });
    setShowCheckinModal(true);
  };

  const handleCheckinSubmit = async (e) => {
    e.preventDefault();
    if (!checkinFormData.name || !checkinFormData.phone || !checkinFormData.room_no) {
      setMessage({ type: 'error', text: 'Guest Name, Phone Number, and Room Number are all required' });
      return;
    }
    if (checkinFormData.room_no.includes(',')) {
      setMessage({ type: 'error', text: 'Only a single room number is allowed per check-in. Do not enter comma-separated rooms.' });
      return;
    }

    const todayIso = new Date().toISOString().split('T')[0];
    const minCheckoutStr = getMinCheckoutDate(todayIso);
    if (checkinFormData.checkout) {
      const checkoutStr = checkinFormData.checkout.split('T')[0];
      if (checkoutStr < minCheckoutStr) {
        setMessage({
          type: 'error',
          text: 'Check-out date cannot be today or yesterday. It must be at least 1 day after check-in date. Please change the check-out date.'
        });
        return;
      }
    }

    try {
      setCheckingIn(true);
      const payload = {
        arrival_id: selectedArrival?.id,
        name: checkinFormData.name,
        phone: checkinFormData.phone,
        room_no: checkinFormData.room_no,
        checkout: checkinFormData.checkout || null,
        sendWelcome: checkinFormData.sendWelcome
      };

      await api.post('/api/admin/guests/checkin-arrival', payload);
      setMessage({ type: 'success', text: `Successfully checked in ${checkinFormData.name} to Room ${checkinFormData.room_no}! Moved to Active Guests.` });
      setShowCheckinModal(false);
      setSelectedArrival(null);
      guestCacheRef.current = {};
      arrivalsCacheRef.current = null;
      setActiveTab('active');
      fetchExpectedArrivals(true);
      await fetchGuests('active', true);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to check in guest' });
    } finally {
      setCheckingIn(false);
    }
  };

  const handleDeleteArrival = async (arrival) => {
    if (!window.confirm(`Remove ${arrival.guest_name} from expected arrivals?`)) return;
    try {
      await api.delete(`/api/admin/guests/expected-arrivals/${arrival.id}`);
      setExpectedArrivals(prev => prev.filter(a => a.id !== arrival.id));
      arrivalsCacheRef.current = null;
      setMessage({ type: 'success', text: 'Arrival record removed' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete arrival record' });
    }
  };

  const handleCheckoutGuest = async (guest) => {
    if (!guest?.id) return;
    const confirmMsg = `Check out ${guest.name || 'this guest'} (Room ${guest.room_no || 'N/A'})? This will move them to Inactive Guests immediately.`;
    if (!window.confirm(confirmMsg)) return;

    try {
      setSubmitting(true);
      await api.post(`/api/admin/guests/${guest.id}/checkout`);
      setMessage({ type: 'success', text: `Successfully checked out ${guest.name || 'Guest'}. Moved to Inactive Guests.` });
      guestCacheRef.current = {};
      await fetchGuests('active', true);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (err) {
      console.error('Failed to checkout guest:', err);
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to check out guest' });
    } finally {
      setSubmitting(false);
    }
  };

  const filteredGuests = guests.filter((guest) => {
    const name = guest.name?.toLowerCase() || '';
    const phone = guest.phone?.toLowerCase() || '';
    const room = guest.room_no?.toString() || '';
    const query = searchQuery.toLowerCase();
    return name.includes(query) || phone.includes(query) || room.includes(query);
  });

  const filteredArrivals = useMemo(() => {
    if (!searchQuery.trim()) return expectedArrivals;
    const q = searchQuery.toLowerCase().trim();
    return expectedArrivals.filter(a => 
      (a.guest_name && a.guest_name.toLowerCase().includes(q)) ||
      (a.resv_no && a.resv_no.toLowerCase().includes(q)) ||
      (a.source && a.source.toLowerCase().includes(q)) ||
      (a.phone && a.phone.includes(q))
    );
  }, [expectedArrivals, searchQuery]);

  const guestCount = filteredGuests.length;
  const totalCount = guests.length;
  const pendingWelcomeCount = filteredGuests.filter((guest) => guest.pendingWelcomeMessage).length;
  const roomAssignedCount = filteredGuests.filter((guest) => Boolean(guest.room_no)).length;
  const todayIso = getDateInputValue(new Date());
  const checkinTodayCount = filteredGuests.filter((guest) => {
    if (!guest.checkin) return false;
    return getDateInputValue(new Date(guest.checkin)) === todayIso;
  }).length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5 px-2 py-3 sm:px-3 sm:py-4 lg:px-1 lg:py-2">
        <div className="space-y-4">
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Guests</h1>
              <p className="text-sm text-gray-500 mt-1">Manage guest profiles, expected arrivals, stays, and communication in one place</p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <button
                onClick={() => {
                  setImportFile(null);
                  setImportPreview([]);
                  setShowImportModal(true);
                }}
                className="bg-white border border-gray-300 text-gray-800 hover:bg-gray-50 px-3 py-2.5 rounded-lg flex items-center justify-center gap-2 transition-all shadow-xs font-semibold text-sm cursor-pointer hover:border-gray-400"
              >
                <FiUploadCloud className="text-indigo-600 flex-shrink-0" size={18} />
                <span className="whitespace-nowrap">Import (.xlsx)</span>
              </button>
              <button
                onClick={() => {
                  setIsEditMode(false);
                  setSelectedGuest(null);
                  setFormData({
                    name: '',
                    phone: '',
                    room_no: '',
                    checkin: getDateInputValue(new Date()),
                    checkout: '',
                    dob: '',
                    anniversary: '',
                    sendWelcome: true
                  });
                  setShowModal(true);
                }}
                className="bg-gray-900 text-white px-4 py-2.5 rounded-lg hover:bg-gray-800 flex items-center justify-center gap-2 transition-all shadow-sm font-medium text-sm cursor-pointer"
              >
                <FiUserPlus size={18} />
                <span>Add Walk-in Guest</span>
              </button>
            </div>
          </div>
          {/* Guest tabs — horizontal scroll on mobile */}
          <div className="mt-4 -mx-1 overflow-x-auto pb-1">
            <div className="flex items-center gap-1 px-1 min-w-max rounded-xl bg-gray-50 border border-gray-200 p-2">
              <button
                onClick={() => {
                  setActiveTab('active');
                  fetchGuests('active');
                }}
                className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${
                  activeTab === 'active'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200 font-bold'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Active Guests
              </button>
              <button
                onClick={() => {
                  setActiveTab('arrivals');
                  fetchExpectedArrivals();
                }}
                className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-all flex items-center gap-1.5 whitespace-nowrap ${
                  activeTab === 'arrivals'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200 font-bold'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span>Expected Arrivals</span>
                {expectedArrivals.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-xs font-extrabold bg-indigo-50 text-indigo-700 border border-indigo-200">
                    {expectedArrivals.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => {
                  setActiveTab('upcoming');
                  fetchGuests('upcoming');
                }}
                className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${
                  activeTab === 'upcoming'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200 font-bold'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Upcoming Guests
              </button>
              <button
                onClick={() => {
                  setActiveTab('inactive');
                  fetchGuests('inactive');
                }}
                className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${
                  activeTab === 'inactive'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200 font-bold'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Inactive Guests
              </button>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full lg:max-w-md">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder={activeTab === 'arrivals' ? "Search by guest name, resv #, or booking source..." : "Search by name, phone, or room number..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="fleet-input pl-10 pr-10"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <FiX size={16} />
                </button>
              )}
            </div>
            <p className="text-sm text-gray-500">
              {activeTab === 'arrivals' 
                ? `Showing ${filteredArrivals.length} of ${expectedArrivals.length} expected arrivals`
                : `Showing ${guestCount} of ${totalCount} ${activeTab} guests`}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <div className="fleet-section">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {activeTab === 'arrivals' ? 'Expected Today' : 'Visible Guests'}
              </p>
              <FiUsers className="text-gray-500" size={16} />
            </div>
            <p className="mt-3 text-2xl font-bold text-gray-900">
              {activeTab === 'arrivals' ? expectedArrivals.length.toLocaleString() : guestCount.toLocaleString()}
            </p>
          </div>
          <div className="fleet-section">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Check-ins Today</p>
              <FiCalendar className="text-gray-500" size={16} />
            </div>
            <p className="mt-3 text-2xl font-bold text-gray-900">{checkinTodayCount.toLocaleString()}</p>
          </div>
          <div className="fleet-section">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Pending Welcome</p>
              <FiClock className="text-gray-500" size={16} />
            </div>
            <p className="mt-3 text-2xl font-bold text-gray-900">{pendingWelcomeCount.toLocaleString()}</p>
          </div>
        </div>

        {/* Message Alert */}
        {message.text && (
          <div
            className={`p-4 rounded-lg border ${
              message.type === 'success'
                ? 'bg-green-50 text-green-800 border-green-200'
                : 'bg-red-50 text-red-800 border-red-200'
            }`}
          >
            <p className="text-sm font-medium">{message.text}</p>
          </div>
        )}

        {/* Expected Arrivals Table (when activeTab === 'arrivals') */}
        {activeTab === 'arrivals' ? (
          <div className="fleet-card overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/70">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-gray-800">Expected Arrivals from PMS</h2>
                <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                  {filteredArrivals.length} pending
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchExpectedArrivals}
                  disabled={arrivalsLoading}
                  className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors text-xs flex items-center gap-1 cursor-pointer"
                  title="Refresh List"
                >
                  <FiRefreshCw className={arrivalsLoading ? 'animate-spin' : ''} size={14} />
                  <span>Refresh</span>
                </button>
              </div>
            </div>
            <div className="overflow-x-auto w-full">
              <table className="w-full text-left border-collapse text-xs table-fixed">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/90 text-gray-600 font-semibold tracking-wider uppercase text-[11px]">
                    <th className="py-3 px-3 w-[50px] lg:w-[60px]">
                      Resv #
                    </th>
                    <th className="py-3 px-3 w-auto min-w-[130px]">
                      Guest Name
                    </th>
                    <th className="py-3 px-3 w-[85px] lg:w-[105px]">
                      Source
                    </th>
                    <th className="py-3 px-3 w-[105px] lg:w-[120px]">
                      Departure
                    </th>
                    <th className="py-3 px-3 w-[130px] lg:w-[145px]">
                      Phone Number
                    </th>
                    <th className="py-3 px-3 w-[135px] lg:w-[150px] text-right">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {filteredArrivals.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <div className="max-w-sm mx-auto flex flex-col items-center">
                          <div className="h-12 w-12 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 mb-3">
                            <FiUploadCloud size={24} />
                          </div>
                          <p className="text-sm text-gray-800 font-semibold mb-1">
                            {searchQuery ? 'No matching expected arrivals found' : 'No expected arrivals imported yet'}
                          </p>
                          <p className="text-xs text-gray-500 mb-4">
                            Upload your hotel PMS arrivals sheet (e.g. Expected Arrivals .xlsx) to instantly populate guest bookings.
                          </p>
                          <button
                            onClick={() => {
                              setImportFile(null);
                              setImportPreview([]);
                              setShowImportModal(true);
                            }}
                            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 font-medium text-xs flex items-center gap-1.5 shadow-sm cursor-pointer"
                          >
                            <FiUploadCloud size={14} />
                            <span>Import Arrivals Excel</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredArrivals.map((arrival) => (
                      <tr key={arrival.id} className="hover:bg-gray-50/80 transition-colors">
                        {/* Column 1: Resv # */}
                        <td className="py-3 px-3 whitespace-nowrap">
                          <span className="font-mono text-[11px] font-bold text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
                            {arrival.resv_no || '—'}
                          </span>
                        </td>

                        {/* Column 2: Guest Name */}
                        <td className="py-3 px-3 min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="h-7 w-7 rounded-full bg-black text-white flex items-center justify-center text-[11px] font-bold shadow-xs shrink-0">
                              {(arrival.guest_name || 'G').replace(/^(mr|ms|mrs|dr)\.?\s+/i, '').charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-semibold text-gray-900 truncate" title={arrival.guest_name}>
                                {arrival.guest_name}
                              </div>
                              {arrival.room_no && (
                                <div className="text-[10px] text-indigo-600 font-medium truncate" title={`Pre-assigned: Room ${arrival.room_no}`}>
                                  Pre-assigned: Room {arrival.room_no}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Column 3: Booking Source / Company */}
                        <td className="py-3 px-3 min-w-0">
                          <span
                            className="block truncate px-2 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-800 border border-gray-200"
                            title={arrival.source || 'Direct Booking'}
                          >
                            {arrival.source || 'Direct'}
                          </span>
                        </td>

                        {/* Column 4: Departure */}
                        <td className="py-3 px-3 text-xs text-gray-700 font-medium truncate" title={formatDepartureDisplay(arrival.departure)}>
                          {formatDepartureDisplay(arrival.departure)}
                        </td>

                        {/* Phone Column */}
                        <td className="py-3 px-3 whitespace-nowrap min-w-0">
                          {arrival.phone ? (
                            <div className="text-xs text-emerald-700 font-semibold flex items-center gap-1 truncate" title={formatPhoneNumber(arrival.phone)}>
                              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0"></span>
                              <span className="truncate">{formatPhoneNumber(arrival.phone)}</span>
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap">
                              Required at check-in
                            </span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="py-3 px-3 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => handleOpenCheckin(arrival)}
                              className="bg-gray-900 hover:bg-black text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5 shadow-xs transition-all cursor-pointer whitespace-nowrap active:scale-95"
                              title="Check In & Assign Room"
                            >
                              <FiCheck size={13} className="text-emerald-400" />
                              <span>Check In</span>
                            </button>
                            <button
                              onClick={() => handleDeleteArrival(arrival)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer shrink-0"
                              title="Remove from expected list"
                            >
                              <FiTrash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Standard Guests Table (Active, Upcoming, Inactive) */
          <div className="fleet-card overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/70">
              <h2 className="text-sm font-semibold text-gray-800">Guest Directory</h2>
              <p className="text-xs text-gray-500">{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} list</p>
            </div>
            <div className="overflow-x-auto w-full">
              <table className="fleet-table w-full">
                <thead>
                  <tr>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Room No
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Check-in
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Check-out
                    </th>
                    <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGuests.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <p className="text-sm text-gray-500 font-medium mb-1">
                        {searchQuery 
                          ? `No ${activeTab} guests found matching your search` 
                            : `No ${activeTab} guests found`}
                        </p>
                        {!searchQuery && (
                          <p className="text-xs text-gray-400">
                            {activeTab === 'active' 
                              ? 'Guests currently staying today (or QR-scanned guests active in the last 24h) will appear here.' 
                              : activeTab === 'upcoming'
                              ? 'Guests with future check-in dates will appear here.'
                              : 'Guests who have checked out (or QR guests inactive for 24h+) will appear here.'}
                          </p>
                        )}
                      </td>
                    </tr>
                  ) : (
                    filteredGuests.map((guest) => {
                      return (
                        <tr key={guest.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded-full bg-black text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                                {(guest.name || 'G').charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div className="text-sm font-semibold text-gray-900">{guest.name}</div>
                                <div className="text-xs text-gray-500">ID: {guest.id?.slice?.(0, 8) || 'N/A'}</div>
                                {guest.stays && guest.stays.length > 1 && (
                                  <div className="mt-1 flex flex-col gap-0.5">
                                    {guest.stays.map((stay, idx) => (
                                      <div key={idx} className="text-[11px] text-gray-600 flex items-center gap-1 font-normal">
                                        <span className="font-semibold text-gray-800">Stay {stay.stay_no || (idx + 1)}:</span>
                                        <span>{formatStaySpan(stay.checkin, stay.checkout)}</span>
                                        {stay.room_no && (
                                          <span className="text-gray-400 font-mono text-[10px]">({stay.room_no})</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {guest.pendingWelcomeMessage && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200 rounded">
                                  Pending Welcome
                                </span>
                              )}
                              {guest.checkin ? (
                                guest.qr_guest && guest.last_active_at ? (
                                  <span
                                    className="px-2 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded flex items-center gap-1"
                                    title={`Scanned room QR code on ${new Date(guest.last_active_at).toLocaleString('en-IN')}`}
                                  >
                                    📱 QR Scanned
                                  </span>
                                ) : (
                                  <span
                                    className="px-1.5 py-0.5 text-[11px] font-normal text-gray-400 border border-gray-200 rounded"
                                    title="Guest has not scanned room QR code yet"
                                  >
                                    QR: Not scanned
                                  </span>
                                )
                              ) : guest.qr_guest ? (
                                <span
                                  className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200 rounded"
                                  title="Connected via QR code scan"
                                >
                                  📱 QR Walk-in
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-700">{formatPhoneNumber(guest.phone)}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-700">{guest.room_no || <span className="text-gray-400">—</span>}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-700">
                              {guest.checkin ? (
                                <div>
                                  <div className="font-medium text-gray-900">{formatDate(guest.checkin)}</div>
                                  {guest.qr_guest && guest.last_active_at && (
                                    <div
                                      className="text-[11px] text-blue-600 font-normal flex items-center gap-1 mt-0.5"
                                      title={`QR scanned at ${new Date(guest.last_active_at).toLocaleString('en-IN')}`}
                                    >
                                      📱 Scanned: {new Date(guest.last_active_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                  )}
                                </div>
                              ) : guest.qr_guest ? (
                                guest.last_active_at ? (
                                  <span className="text-blue-600 text-xs font-medium" title="QR scanned at">
                                    📱 QR Scanned at {new Date(guest.last_active_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">QR Guest</span>
                                )
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-700 flex items-center gap-2">
                              <span>
                                {guest.checkout
                                  ? formatDate(guest.checkout)
                                  : <span className="text-gray-400">—</span>}
                              </span>
                              {activeTab === 'active' && guest.checkout && isSameDayIST(guest.checkout, new Date()) && (
                                <button
                                  onClick={() => handleCheckoutGuest(guest)}
                                  disabled={submitting}
                                  className="px-2 py-0.5 text-xs font-medium bg-black text-white hover:bg-gray-800 rounded transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-1"
                                  title="Check out guest now to move to Inactive Guests"
                                >
                                  <FiLogOut size={11} />
                                  Check Out
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {guest.isWelcomeFailed ? (
                                <div className="flex items-center gap-1.5">
                                  <span 
                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-red-100 text-red-700 border border-red-200 cursor-help"
                                    title={getWhatsAppErrorMessage(guest.welcomeMessageError)}
                                  >
                                    <FiAlertCircle size={12} className="text-red-600 shrink-0" />
                                    Failed {guest.welcomeMessageError?.code ? `(${guest.welcomeMessageError.code})` : ''}
                                  </span>
                                  <button
                                    onClick={() => handleSendWelcomeMessage(guest)}
                                    disabled={sendingWelcomeGuestId === guest.id}
                                    className="px-2.5 py-1 text-xs font-medium bg-red-600 text-white rounded hover:bg-red-700 transition-colors flex items-center gap-1 disabled:opacity-50"
                                    title="Retry sending Welcome Message"
                                  >
                                    <FiRefreshCw size={11} className={sendingWelcomeGuestId === guest.id ? 'animate-spin' : ''} />
                                    Retry
                                  </button>
                                </div>
                              ) : (guest.welcomeMessageStatus === 'delivered' || guest.welcomeMessageStatus === 'read') ? (
                                <span 
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-green-50 text-green-700 border border-green-200"
                                  title={guest.welcomeMessageStatus === 'read' ? 'Guest read the welcome message' : 'Delivered to guest WhatsApp device'}
                                >
                                  <FiCheckCircle size={12} className="text-green-600 shrink-0" />
                                  {guest.welcomeMessageStatus === 'read' ? 'Read' : 'Delivered'}
                                </span>
                              ) : guest.welcomeMessageStatus === 'sent' ? (
                                <span 
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-amber-50 text-amber-700 border border-amber-200"
                                  title="Dispatched to WhatsApp, waiting for delivery confirmation"
                                >
                                  <FiClock size={12} className="text-amber-600 shrink-0" />
                                  Sent
                                </span>
                              ) : guest.pendingWelcomeMessage && (
                                <button
                                  onClick={() => handleSendWelcomeMessage(guest)}
                                  disabled={sendingWelcomeGuestId === guest.id}
                                  className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Send Welcome Message"
                                >
                                  {sendingWelcomeGuestId === guest.id ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                      Sending...
                                    </>
                                  ) : (
                                    <>
                                      <FiSend size={12} />
                                      Send Welcome
                                    </>
                                  )}
                                </button>
                              )}
                              <button
                                onClick={() => handleViewDetails(guest)}
                                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors cursor-pointer"
                                title="View Details"
                              >
                                <FiEye size={16} />
                              </button>
                              <button
                                onClick={() => handleEdit(guest)}
                                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors cursor-pointer"
                                title="Edit Guest"
                              >
                                <FiEdit2 size={16} />
                              </button>
                              <button
                                onClick={() => handleSendMessage(guest)}
                                className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded transition-colors cursor-pointer"
                                title="Send Message"
                              >
                                <FiMessageSquare size={16} />
                              </button>
                              {authUser?.role === 'admin' && (
                                <button
                                  onClick={() => handleDelete(guest)}
                                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                                  title="Delete Guest"
                                >
                                  <FiTrash2 size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add/Edit Guest Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">
                  {isEditMode ? 'Edit Guest' : 'Add Walk-in Guest'}
                </h2>
                <button
                  onClick={handleModalClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX size={24} />
                </button>
              </div>
              
              {message.text && (
                <div
                  className={`mb-4 p-3 rounded-lg border ${
                    message.type === 'success'
                      ? 'bg-green-50 text-green-800 border-green-200'
                      : 'bg-red-50 text-red-800 border-red-200'
                  }`}
                >
                  {message.text}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300"
                      required
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setFormData(prev => ({
                          ...prev,
                          phone: value
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300"
                      required={!isEditMode}
                      placeholder="9876543210"
                      maxLength={10}
                      pattern="[6-9]\d{9}"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Enter 10-digit phone number (country code 91 will be added automatically)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Room Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="room_no"
                      value={formData.room_no}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300"
                      placeholder="101"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Check-in Date {!isEditMode && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="date"
                      name="checkin"
                      value={formData.checkin}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300"
                      required={!isEditMode}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Check-out Date {!isEditMode && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="date"
                      name="checkout"
                      value={formData.checkout}
                      onChange={handleInputChange}
                      min={getMinCheckoutDate(formData.checkin || new Date().toISOString().split('T')[0])}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300"
                      required={!isEditMode}
                    />
                  </div>



                  {!isEditMode && (
                    <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="sendWelcome"
                      id="sendWelcome"
                      checked={formData.sendWelcome}
                      onChange={handleInputChange}
                        className="h-4 w-4 text-gray-900 focus:ring-gray-300 border-gray-300 rounded"
                    />
                    <label htmlFor="sendWelcome" className="ml-2 block text-sm text-gray-700">
                      Send welcome message via WhatsApp
                    </label>
                  </div>
                  )}
                </div>

                <div className="flex justify-end space-x-2 mt-6">
                  <div className="flex justify-between items-center w-full">
                    <button
                      type="button"
                      onClick={handleModalClose}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                    {lastCreatedRoom && !isEditMode && (
                      <p className="text-xs text-gray-600 font-medium">
                        Adding guests to Room {lastCreatedRoom}
                      </p>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    disabled={submitting}
                  >
                    {submitting ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Guest' : 'Add Walk-in Guest')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Guest Details Modal */}
        {showDetailsModal && selectedGuest && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">Guest Details</h2>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedGuest(null);
                    setGuestDetails(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX size={24} />
                </button>
              </div>

              {detailsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>
              ) : guestDetails ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase">Name</label>
                      <p className="text-sm font-medium text-gray-900 mt-1">{guestDetails.name}</p>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase">Phone</label>
                      <p className="text-sm font-medium text-gray-900 mt-1">{formatPhoneNumber(guestDetails.phone)}</p>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase">Room Number</label>
                      <p className="text-sm font-medium text-gray-900 mt-1">{guestDetails.room_no || '—'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase">Check-in</label>
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        {guestDetails.checkin ? (
                          formatDate(guestDetails.checkin)
                        ) : guestDetails.qr_guest && guestDetails.last_active_at ? (
                          <span className="text-blue-600 font-medium">
                            📱 QR Scanned at {new Date(guestDetails.last_active_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        ) : (
                          '—'
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase">Check-out</label>
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        {guestDetails.checkout ? formatDate(guestDetails.checkout) : '—'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase">Room QR Status</label>
                      <p className="text-sm font-medium mt-1">
                        {guestDetails.qr_guest && guestDetails.last_active_at ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 text-xs font-medium">
                            📱 Scanned ({new Date(guestDetails.last_active_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })})
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">Not scanned</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase">Created</label>
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        {guestDetails.created_at ? formatDate(guestDetails.created_at) : '—'}
                      </p>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <FiClipboard className="text-gray-600" size={18} />
                          <label className="text-xs font-semibold text-gray-500 uppercase">Service Requests</label>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{guestDetails.serviceRequestsCount || 0}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <FiMessageSquare className="text-gray-600" size={18} />
                          <label className="text-xs font-semibold text-gray-500 uppercase">Messages</label>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{guestDetails.messagesCount || 0}</p>
                      </div>
                    </div>

                    {guestDetails.stays && guestDetails.stays.length > 1 && (
                      <div className="mb-4 bg-gray-50 p-3 rounded-lg border border-gray-100">
                        <label className="text-xs font-semibold text-gray-500 uppercase mb-2 block flex items-center gap-1.5">
                          <span>All Stays ({guestDetails.stays.length})</span>
                        </label>
                        <div className="space-y-1.5">
                          {guestDetails.stays.map((stay, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs py-1 border-b border-gray-100 last:border-0">
                              <span className="font-semibold text-gray-800">Stay {stay.stay_no || (idx + 1)}</span>
                              <span className="text-gray-700">{formatStaySpan(stay.checkin, stay.checkout)}</span>
                              <span className="text-gray-500 font-mono">Room: {stay.room_no || '—'}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {guestDetails.serviceRequests && guestDetails.serviceRequests.length > 0 && (
                      <div className="mt-4">
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Recent Service Requests</h3>
                        <div className="space-y-2">
                          {guestDetails.serviceRequests.slice(0, 5).map((req) => (
                            <div key={req.id} className="bg-gray-50 p-3 rounded-lg">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{req.category}</p>
                                  <p className="text-xs text-gray-500 mt-1">{req.details}</p>
                                </div>
                                <span className={`text-xs px-2 py-1 rounded ${
                                  req.status === 'completed' ? 'bg-green-100 text-green-800' :
                                  req.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {req.status}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">Failed to load guest details</p>
              )}
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedGuest && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Delete Guest</h2>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete <strong>{selectedGuest.name}</strong>? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedGuest(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  disabled={submitting}
                >
                  {submitting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Send Message Modal */}
        {showMessageModal && selectedGuest && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">Send Message</h2>
                <button
                  onClick={() => {
                    setShowMessageModal(false);
                    setSelectedGuest(null);
                    setMessageText('');
                    setMessage({ type: '', text: '' });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX size={24} />
                </button>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Send a WhatsApp message to <strong>{selectedGuest.name}</strong> ({formatPhoneNumber(selectedGuest.phone)})
              </p>

              {message.text && (
                <div
                  className={`mb-4 p-3 rounded-lg border ${
                    message.type === 'success'
                      ? 'bg-green-50 text-green-800 border-green-200'
                      : 'bg-red-50 text-red-800 border-red-200'
                  }`}
                >
                  {message.text}
                </div>
              )}

              <form onSubmit={handleSendMessageSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300"
                    placeholder="Type your message here..."
                    required
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowMessageModal(false);
                      setSelectedGuest(null);
                      setMessageText('');
                      setMessage({ type: '', text: '' });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                    disabled={sendingMessage}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 cursor-pointer"
                    disabled={sendingMessage || !messageText.trim()}
                  >
                    <FiSend size={16} />
                    {sendingMessage ? 'Sending...' : 'Send Message'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Import Arrivals Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
              <div className="flex justify-between items-center pb-4 border-b border-gray-100">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <FiUploadCloud className="text-indigo-600" size={22} />
                    <span>Import Expected Arrivals</span>
                  </h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Upload your PMS expected arrivals report (.xlsx, .xls) to auto-extract reservations and guest names.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                    setImportPreview([]);
                  }}
                  className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <FiX size={20} />
                </button>
              </div>

              <div className="py-5 overflow-y-auto space-y-4 flex-1">
                {/* File Dropzone */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 hover:border-indigo-500 rounded-xl p-8 text-center bg-gray-50/60 hover:bg-indigo-50/20 transition-all cursor-pointer group"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx, .xls, .csv"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <div className="h-12 w-12 rounded-full bg-white shadow-xs border border-gray-200 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                    <FiFileText className="text-indigo-600" size={24} />
                  </div>
                  <p className="text-sm font-semibold text-gray-800">
                    {importFile ? importFile.name : 'Click or Drag & Drop PMS Excel File'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Supports Oleander PMS, IDS Next, Opera, and standard Excel arrival sheets (.xlsx, .xls)
                  </p>
                </div>

                {/* Parsed Preview */}
                {importPreview.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5">
                        <FiCheckCircle className="text-emerald-500" size={15} />
                        <span>Detected {importPreview.length} Guest Arrivals</span>
                      </p>
                      <span className="text-xs text-gray-500">Ready to import</span>
                    </div>

                    <div className="border border-gray-200 rounded-xl overflow-hidden max-h-56 overflow-y-auto">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-gray-100/80 text-gray-700 font-semibold sticky top-0">
                          <tr>
                            <th className="px-3 py-2">Resv #</th>
                            <th className="px-3 py-2">Guest Name</th>
                            <th className="px-3 py-2">Source</th>
                            <th className="px-3 py-2">Departure</th>
                            <th className="px-3 py-2">Phone</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {importPreview.map((item, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-3 py-2 font-mono text-gray-600">{item.resv_no || '—'}</td>
                              <td className="px-3 py-2 font-semibold text-gray-900">{item.guest_name}</td>
                              <td className="px-3 py-2 text-gray-600">{item.source || 'Direct'}</td>
                              <td className="px-3 py-2 text-gray-600">{item.departure || '—'}</td>
                              <td className="px-3 py-2 text-gray-600">{item.phone ? formatPhoneNumber(item.phone) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-gray-100 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                    setImportPreview([]);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                  disabled={importing}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmImport}
                  disabled={importPreview.length === 0 || importing}
                  className="px-5 py-2 text-sm font-semibold bg-gray-900 hover:bg-black text-white rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 cursor-pointer"
                >
                  {importing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Importing...</span>
                    </>
                  ) : (
                    <>
                      <FiUploadCloud size={16} />
                      <span>Confirm & Import ({importPreview.length})</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quick Check-In Modal */}
        {showCheckinModal && selectedArrival && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
              <div className="flex justify-between items-start pb-4 border-b border-gray-100">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Check In Guest</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Assign room and send instant WhatsApp welcome message</p>
                </div>
                <button
                  onClick={() => {
                    setShowCheckinModal(false);
                    setSelectedArrival(null);
                  }}
                  className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <FiX size={20} />
                </button>
              </div>

              {/* Arrival Summary Card */}
              <div className="mt-4 p-3.5 bg-gray-50 rounded-xl border border-gray-200/70 text-xs space-y-1.5">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 font-medium">Guest Name:</span>
                  <span className="font-bold text-gray-900 text-sm">{selectedArrival.guest_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 font-medium">Reservation #:</span>
                  <span className="font-mono font-semibold text-gray-800">{selectedArrival.resv_no || 'N/A'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 font-medium">Booking Source:</span>
                  <span className="font-semibold text-gray-800">{selectedArrival.source || 'Direct'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 font-medium">Departure:</span>
                  <span className="font-semibold text-gray-800">{selectedArrival.departure || '—'}</span>
                </div>
              </div>

              <form onSubmit={handleCheckinSubmit} className="mt-5 space-y-4">
                {/* Guest Name Field */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                    Guest Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={checkinFormData.name}
                    onChange={(e) => setCheckinFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="fleet-input text-sm"
                    required
                    placeholder="Guest Full Name"
                  />
                </div>

                {/* Phone Number Field */}
                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Mobile Number (+91) <span className="text-red-500">*</span>
                    </label>
                    {selectedArrival.phone && (
                      <span className="text-[10px] text-emerald-600 font-semibold bg-emerald-50 px-1.5 py-0.5 rounded">
                        From PMS (Verify)
                      </span>
                    )}
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-semibold">
                      +91
                    </span>
                    <input
                      type="tel"
                      value={checkinFormData.phone}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setCheckinFormData(prev => ({ ...prev, phone: val }));
                      }}
                      className="fleet-input pl-12 text-sm"
                      required
                      placeholder="9876543210"
                      maxLength={10}
                      pattern="[6-9]\d{9}"
                    />
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">
                    {selectedArrival.phone
                      ? 'Please verify mobile number before sending the welcome message.'
                      : 'Ask the guest for their 10-digit mobile number at check-in.'}
                  </p>
                </div>

                {/* Room Number Assignment */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                    Assign Room Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={checkinFormData.room_no}
                    onChange={(e) => {
                      const val = e.target.value.replace(/,/g, '');
                      setCheckinFormData(prev => ({ ...prev, room_no: val }));
                    }}
                    className="fleet-input text-sm"
                    required
                    placeholder="e.g. 101"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">
                    Enter a single room number only (comma-separated rooms are not allowed).
                  </p>
                </div>

                {/* Check-out Date */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                    Check-out Date (Departure)
                  </label>
                  <input
                    type="date"
                    value={checkinFormData.checkout}
                    onChange={(e) => {
                      const val = e.target.value;
                      const todayIso = new Date().toISOString().split('T')[0];
                      const minCheckout = getMinCheckoutDate(todayIso);
                      if (val && val < minCheckout) {
                        setMessage({
                          type: 'error',
                          text: 'Check-out date cannot be today or in the past. It must be at least 1 day after check-in.'
                        });
                      } else {
                        setMessage({ type: '', text: '' });
                      }
                      setCheckinFormData(prev => ({ ...prev, checkout: val }));
                    }}
                    min={getMinCheckoutDate(new Date().toISOString().split('T')[0])}
                    className="fleet-input text-sm"
                  />
                </div>

                {/* Send WhatsApp Welcome Toggle */}
                <div className="flex items-center gap-3 pt-2 p-3 bg-gray-50 rounded-xl border border-gray-200/80">
                  <input
                    type="checkbox"
                    id="checkinSendWelcome"
                    checked={checkinFormData.sendWelcome}
                    onChange={(e) => setCheckinFormData(prev => ({ ...prev, sendWelcome: e.target.checked }))}
                    className="h-4 w-4 text-gray-900 rounded border-gray-300 focus:ring-gray-400 cursor-pointer"
                  />
                  <label htmlFor="checkinSendWelcome" className="text-xs font-medium text-gray-800 cursor-pointer select-none">
                    Send instant WhatsApp welcome message upon check-in
                  </label>
                </div>

                <div className="pt-3 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCheckinModal(false);
                      setSelectedArrival(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                    disabled={checkingIn}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={checkingIn || !checkinFormData.name || !checkinFormData.phone || !checkinFormData.room_no}
                    className="px-5 py-2.5 bg-gray-900 hover:bg-black text-white text-sm font-semibold rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 cursor-pointer"
                  >
                    {checkingIn ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        <span>Checking In...</span>
                      </>
                    ) : (
                      <>
                        <FiCheck size={16} />
                        <span>Check In & Welcome Guest</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
}

export default Guests;

